import Box from "@mui/material/Box";
import BottomBar from "./BottomBar";
import MainContent from "./MainContent";
import EditableText from "../../../../components/Texts/EditableText";
import { useFormContext } from "react-hook-form";
import {
  HISTORICAL_CHANGES,
  REPORTS,
  ERROR_GENERATING_CAMPAIGN,
  BUTTON_TRY_AGAIN,
  SYNCING_INFORMATION_NO_EDIT_CLARIFICATION,
  AGENT_LABEL,
  ID_PLATFORM,
  READY,
} from "../../../../i18n/keysTranslations";
import ButtonDuplicateCampaign from "../../../../components/Buttons/ButtonDuplicateCampaign";
import IconButton from "../../../../components/Buttons/IconButton";
import { openLink } from "../../../../utils/string";
import { useEffect, useState } from "react";
import IconButtonExportCopies from "../../../../components/Buttons/IconButtonExportCopies";
import { Tooltip, Typography } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslationApp } from "../../../../lib/i18next";
import Button from "../../../../components/Buttons/Button";
import Loader from "../../../../components/Loaders/Loader";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { ChartColumnBig, Sparkles } from "lucide-react";
import ModalCampaingLogs from "../CampaignLogs/ModalCampaingLogs";
import AlertBanner from "../../../../components/Alerts/AlertBanner";
import { useSelectorApp } from "../../../../lib/redux";
import {
  ADS_CAMPAIGN_STATUS_DRAFT,
  ALERT_ICON_TYPE_SUCCESS,
} from "../../../../utils/constants";
import { hasRoleCapability } from "../../../../utils/roleCapabilities";
import { SimpleAlert } from "../../../../components/Alerts/Alerts";
import CampaignApprovalControls from "../CampaignApproval/CampaignApprovalControls";

const GeneralContainer = () => {
  const [name, setName] = useState("");
  const [idPlatform, setIdPlatform] = useState("");
  const [isModalLogsOpen, setIsModalLogsOpen] = useState(false);
  const { t } = useTranslationApp();
  const { user } = useSelectorApp((state) => state.auth);
  const {
    isAllCampaignsPlatformCreated,
    setValue,
    watch,
    levelSectionSelected,
    sectionSelected,
    showErrorGenerating,
    lastInformationToGenerate,
    onGenerateCampaignPlatform,
    getValues,
    campaignID,
    isForcedSetupPage,
    isGeneralDisabledFields,
    hasSomePlatformCreated,
    onToggleChat,
    isSyncingData,
    isViewOnboarding,
    onChangeSectionSelected,
    campaignApproval,
    isEnablingCampaignApproval,
    onEnableCampaignApproval,
    onCopyCampaignApprovalLink,
    onOpenCampaignApprovalCommentsPanel,
  } = useFormContext();

  const platformsData = watch("platformsData") || {};
  const platforms = Object.keys(platformsData);
  const campaignPlatforms = watch("platforms") || [];
  const states = watch("states") || {};
  const shouldShowCampaignApprovalControls =
    campaignPlatforms.length > 0 &&
    campaignPlatforms.every(
      (platform) => states[platform] === ADS_CAMPAIGN_STATUS_DRAFT
    );

  useEffect(() => {
    setIdPlatform("");
    if (levelSectionSelected === "platform") {
      const states = getValues("states");
      const state = states[sectionSelected.platform];

      if (state !== ADS_CAMPAIGN_STATUS_DRAFT) {
        const platformsIDs = getValues("platformIDs");
        const idPlatform = platformsIDs[sectionSelected.platform];
        setIdPlatform(idPlatform || "");
      }

      const platformData = platformsData[sectionSelected.platform];
      const nameCampaign = platformData?.name;
      const nameCampaigns = getValues("name");
      if (!nameCampaign && nameCampaigns) {
        setValue(
          `platformsData.${sectionSelected.platform}.name`,
          nameCampaigns
        );
        setName(nameCampaigns);
      } else {
        setName(nameCampaign);
      }
    }
    if (levelSectionSelected === "assetsGroup") {
      const assetsGroups = getValues("assetsGroups");
      const assetsGroup = assetsGroups.find(
        (assetsGroup) => assetsGroup.id === sectionSelected.assetsGroupID
      );
      const nameAssetGroup = assetsGroup.name;
      setName(nameAssetGroup);

      const status = assetsGroup.status;
      if (status !== ADS_CAMPAIGN_STATUS_DRAFT) {
        setIdPlatform(assetsGroup.idPlatform || "");
      }
    }
    if (levelSectionSelected === "adGroup") {
      const adsGroups = getValues("adsGroups");
      const adGroup = adsGroups.find(
        (adGroup) => adGroup.id === sectionSelected.adGroupID
      );
      const nameAdGroup = adGroup.name;
      setName(nameAdGroup);

      const status = adGroup.status;
      if (status !== ADS_CAMPAIGN_STATUS_DRAFT) {
        const idPlatform = adGroup.idPlatform;
        setIdPlatform(idPlatform || "");
      }
    }
    if (levelSectionSelected === "ad") {
      const adsGroups = getValues("adsGroups");
      const adGroup = adsGroups.find(
        (adGroup) => adGroup.id === sectionSelected.adGroupID
      );
      const ads = adGroup.ads;
      const ad = ads.find((ad) => ad.id === sectionSelected.adID);
      const nameAd = ad.name;
      setName(nameAd);

      const status = ad.status;
      if (status !== ADS_CAMPAIGN_STATUS_DRAFT) {
        setIdPlatform(ad.idPlatform || "");
      }
    }
    // eslint-disable-next-line
  }, [levelSectionSelected, sectionSelected]);

  const onChangeName = (newValue) => {
    if (levelSectionSelected === "platform") {
      setValue(`platformsData.${sectionSelected.platform}.name`, newValue);
      setValue(
        `platformsData.${sectionSelected.platform}.nameLowercase`,
        String(newValue ?? "")
          .trim()
          .toLowerCase()
      );
    }

    if (levelSectionSelected === "assetsGroup") {
      const assetsGroups = getValues("assetsGroups");
      const assetsGroupIndex = assetsGroups.findIndex(
        (assetsGroup) => assetsGroup.id === sectionSelected.assetsGroupID
      );
      setValue(`assetsGroups.[${assetsGroupIndex}].name`, newValue);
    }

    if (levelSectionSelected === "adGroup") {
      const adsGroups = getValues("adsGroups");
      const adGroupIndex = adsGroups.findIndex(
        (adGroup) => adGroup.id === sectionSelected.adGroupID
      );
      setValue(`adsGroups.[${adGroupIndex}].name`, newValue);
    }

    if (levelSectionSelected === "ad") {
      const adsGroups = getValues("adsGroups");
      const adGroupIndex = adsGroups.findIndex(
        (adGroup) => adGroup.id === sectionSelected.adGroupID
      );
      const ads = adsGroups[adGroupIndex].ads;
      const adIndex = ads.findIndex((ad) => ad.id === sectionSelected.adID);
      setValue(`adsGroups.[${adGroupIndex}].ads.[${adIndex}].name`, newValue);
    }
  };

  const onRemoveAlert = (platform, alertType, originalIndex) => {
    const currentPlatformData = platformsData[platform];
    if (
      !currentPlatformData?.alerts ||
      !Array.isArray(currentPlatformData.alerts)
    ) {
      return;
    }

    const targetEntity =
      levelSectionSelected === "platform" ? "campaign" : levelSectionSelected;
    const alertsArray = currentPlatformData.alerts;

    // Caso ideal: el índice original apunta exactamente a la alerta correcta
    const candidate = alertsArray[originalIndex];
    let indexToRemove = -1;
    if (
      candidate &&
      candidate.type === alertType &&
      candidate.entity === targetEntity
    ) {
      indexToRemove = originalIndex;
    } else {
      // Respaldo: buscar por coincidencia de type y entity
      indexToRemove = alertsArray.findIndex(
        (alert) => alert?.type === alertType && alert?.entity === targetEntity
      );
    }

    if (indexToRemove < 0) {
      return;
    }

    const updatedAlerts = [
      ...alertsArray.slice(0, indexToRemove),
      ...alertsArray.slice(indexToRemove + 1),
    ];

    setValue(`platformsData.${platform}.alerts`, updatedAlerts);
  };

  const showExportCopies =
    levelSectionSelected === "ad" || levelSectionSelected === "assetsGroup";

  const renderLeftButtonGroup = (isMobile = false) => (
    <Box sx={{ display: "flex", gap: isMobile ? 1 : 1.5 }}>
      {name &&
        !isGeneralDisabledFields &&
        levelSectionSelected === "platform" && (
          <ButtonDuplicateCampaign campaignID={campaignID} />
        )}
      {showExportCopies && (
        <IconButtonExportCopies
          campaignID={campaignID}
          platform={sectionSelected.platform}
          assetGroupID={sectionSelected.assetsGroupID}
          adID={sectionSelected.adID}
        />
      )}
      {hasSomePlatformCreated && (
        <IconButton
          edge="end"
          onClick={() => {
            setIsModalLogsOpen(true);
          }}
          tooltipText={HISTORICAL_CHANGES}
        >
          <HistoryIcon
            sx={{
              width: 26,
              height: 26,
            }}
          />
        </IconButton>
      )}
    </Box>
  );

  const renderRightButtonGroup = () => (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
        justifyContent: "flex-end",
      }}
    >
      {isSyncingData && (
        <Tooltip
          title={t(SYNCING_INFORMATION_NO_EDIT_CLARIFICATION)}
          arrow
          placement="bottom-start"
          componentsProps={{
            tooltip: {
              sx: {
                borderRadius: 3,
                mt: "0px !important",
                backgroundColor: "background.paper",
                border: `2px solid ${INPUT_COLOR_BORDER}`,
                color: (theme) => `${theme.palette.text.primary} !important`,
              },
            },
            arrow: {
              sx: {
                "&::before": {
                  color: "background.paper",
                  border: `2px solid ${INPUT_COLOR_BORDER}`,
                },
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mr: 2,
            }}
          >
            <Loader hasMarginTop={false} size={20} />
          </Box>
        </Tooltip>
      )}
      {!isViewOnboarding &&
      hasRoleCapability(user?.role, "canUseCampaignAgent") &&
      shouldShowCampaignApprovalControls ? (
        <CampaignApprovalControls
          approvalData={campaignApproval}
          isEnabling={isEnablingCampaignApproval}
          disabled={isGeneralDisabledFields}
          onEnableApproval={onEnableCampaignApproval}
          onCopyLink={onCopyCampaignApprovalLink}
          onViewComments={onOpenCampaignApprovalCommentsPanel}
        />
      ) : null}
      {isAllCampaignsPlatformCreated && (
        <Button
          startIcon={<ChartColumnBig size={20} />}
          color="secondary"
          onClick={() => {
            openLink(`/ads-campaign-statistics?campaignID=${campaignID}`);
          }}
        >
          {t(REPORTS)}
        </Button>
      )}

      {!isViewOnboarding &&
        hasRoleCapability(user?.role, "canUseCampaignAgent") && (
          <Button
            startIcon={<Sparkles size={20} />}
            color="secondary"
            onClick={onToggleChat}
            sx={{
              minWidth: "max-content",
            }}
          >
            {t(AGENT_LABEL)}
          </Button>
        )}
    </Box>
  );

  const onCopyIdPlatform = async () => {
    if (!idPlatform) {
      return;
    }

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(idPlatform).catch(() => {});
      SimpleAlert({
        title: t(READY),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      return;
    }
  };

  if (showErrorGenerating && isViewOnboarding) {
    return (
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="h5">{t(ERROR_GENERATING_CAMPAIGN)}</Typography>
        <Button
          onClick={() => onGenerateCampaignPlatform(lastInformationToGenerate)}
        >
          {t(BUTTON_TRY_AGAIN)}
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
          pt: levelSectionSelected ? 3 : 0,
        }}
      >
        {!isForcedSetupPage && (
          <Box
            sx={{
              mx: { xs: 1.75, md: 3 },
              mb: isSyncingData ? 2 : 0,
            }}
          >
            <AlertBanner
              platformsData={platformsData}
              sectionSelected={sectionSelected}
              levelSectionSelected={levelSectionSelected}
              currentLevel={levelSectionSelected}
              onRemoveAlert={onRemoveAlert}
            />

            {/* Layout for desktop */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                {name && (
                  <EditableText
                    value={name}
                    variantText="h6"
                    disabledEditable={isGeneralDisabledFields}
                    onCallbackSave={onChangeName}
                    sx={{
                      maxWidth: 290,
                    }}
                    sxText={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                )}
                {renderLeftButtonGroup()}
              </Box>
              {renderRightButtonGroup()}
            </Box>

            {/* Layout for mobile */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {/* EditableText in its own row */}
              {name && (
                <EditableText
                  value={name}
                  variantText="h6"
                  disabledEditable={isGeneralDisabledFields}
                  onCallbackSave={onChangeName}
                  sx={{
                    maxWidth: "100%",
                  }}
                  sxText={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                />
              )}

              {/* Buttons in a separate row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {renderLeftButtonGroup(true)}
                {renderRightButtonGroup()}
              </Box>
            </Box>
            {idPlatform && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "text.secondary",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    mr: 2,
                  }}
                >
                  {`${t(ID_PLATFORM, { value: t(sectionSelected.platform) })}: ${idPlatform}`}
                </Typography>
                <IconButton
                  edge="start"
                  onClick={onCopyIdPlatform}
                  sx={{
                    p: 0.25,
                    color: "text.secondary",
                    "&:hover": {
                      color: "text.secondary",
                    },
                  }}
                >
                  <ContentCopyIcon
                    sx={{
                      fontSize: 13,
                    }}
                  />
                </IconButton>
              </Box>
            )}
          </Box>
        )}

        <MainContent />

        {platforms?.length > 0 && <BottomBar />}
      </Box>

      <ModalCampaingLogs
        modalOpen={isModalLogsOpen}
        onCloseModal={() => setIsModalLogsOpen(false)}
        enableReverse={true}
        getValues={getValues}
        setValue={setValue}
        onChangeSectionSelected={onChangeSectionSelected}
      />
    </>
  );
};

export default GeneralContainer;
